/**
 * Наклон устройства.
 *
 * Зачем отдельный модуль: голый DeviceOrientationEvent внутри Telegram
 * на iOS не работает. С iOS 15 WKWebView отдаёт события только если
 * хост-приложение реализовало запрос разрешения; иначе requestPermission()
 * молча резолвится в 'denied' — без промпта, без ошибки, без событий.
 * Отладить это со стороны веба нельзя: код выглядит рабочим.
 *
 * Telegram обошёл это своим API в Bot API 8.0. Он и есть единственный
 * надёжный источник наклона внутри мини-аппа.
 *
 * Наружу отдаём НЕ градусы, а нормализованное отклонение −1…1 от того,
 * как человек держит телефон прямо сейчас. Абсолютные градусы бесполезны:
 * лёжа в кровати beta около 0, сидя за столом — около 45, и эффект,
 * настроенный на одну позу, во второй просто не включится.
 */

import { supports, webApp } from './webapp'

export interface Tilt {
  /** Влево-вправо. −1 — наклон влево, 1 — вправо. */
  x: number
  /** От себя — к себе. −1 — экран завален от себя, 1 — на себя. */
  y: number
}

/** Отклонение в градусах, которое считается полным (даёт ±1). */
const RANGE_DEG = 22

/**
 * Частота опроса, мс. 30 Гц.
 *
 * Не 60: гироскоп — реальный расход батареи, а перелив по определению
 * медленный. Telegram документирует, что фактическая частота может
 * отличаться от заданной, поэтому сглаживание ниже обязательно.
 */
const REFRESH_MS = 33

/**
 * Коэффициент экспоненциального сглаживания.
 *
 * Сырой гироскоп дрожит даже в неподвижной руке. Без фильтра блик
 * на карте мелко трясётся — читается как дефект, а не как материал.
 */
const SMOOTHING = 0.18

/** Доступен ли наклон. Проверяется и версия, и наличие объекта. */
export const tiltSupported = (): boolean =>
  supports('8.0') && typeof webApp()?.DeviceOrientation?.start === 'function'

/**
 * Подписаться на наклон. Возвращает функцию отписки.
 *
 * Если наклон недоступен — возвращает no-op и НЕ вызывает onTilt ни разу.
 * Вызывающий по этому признаку включает свой запасной источник (палец):
 * молчание здесь однозначно, в отличие от одного нулевого события.
 */
export function watchTilt(onTilt: (tilt: Tilt) => void): () => void {
  const tg = webApp()
  const sensor = tg?.DeviceOrientation

  if (!tg || !sensor || !tiltSupported() || !tg.onEvent || !tg.offEvent) {
    return () => {}
  }

  // Точка отсчёта — первая пришедшая поза. Всё остальное меряем от неё.
  let baseBeta: number | null = null
  let baseGamma: number | null = null
  let smooth: Tilt = { x: 0, y: 0 }
  let stopped = false

  const clamp = (v: number): number => Math.max(-1, Math.min(1, v))

  const onChange = (): void => {
    const { beta, gamma } = sensor
    if (beta == null || gamma == null) return

    if (baseBeta == null || baseGamma == null) {
      baseBeta = beta
      baseGamma = gamma
      return
    }

    const target: Tilt = {
      x: clamp((gamma - baseGamma) / RANGE_DEG),
      y: clamp((beta - baseBeta) / RANGE_DEG),
    }

    smooth = {
      x: smooth.x + (target.x - smooth.x) * SMOOTHING,
      y: smooth.y + (target.y - smooth.y) * SMOOTHING,
    }
    onTilt(smooth)
  }

  // Датчик не поддержан на конкретном устройстве — тихо уходим.
  // Вызывающий уже включил запасной источник: onTilt так и не сработал.
  const onFailed = (): void => stop()

  const stop = (): void => {
    if (stopped) return
    stopped = true
    tg.offEvent?.('deviceOrientationChanged', onChange)
    tg.offEvent?.('deviceOrientationFailed', onFailed)
    sensor.stop()
  }

  tg.onEvent('deviceOrientationChanged', onChange)
  tg.onEvent('deviceOrientationFailed', onFailed)
  sensor.start({ refresh_rate: REFRESH_MS, need_absolute: false })

  return stop
}

/**
 * Сбросить точку отсчёта нельзя изнутри — только переподпиской.
 *
 * Это осознанно: перекалибровка посреди жеста выглядит как рывок блика.
 * Человек меняет позу редко, а подписка живёт ровно один экран.
 */
